import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Image, 
  Briefcase, 
  FileText, 
  MessageSquare, 
  Settings, 
  LogOut, 
  AlignLeft,
  X,
  Type,
  Users,
  Database,
  UserPlus,
  FolderOpen,
  Info
} from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Default: Closed on mobile (< 768px), Open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const user = JSON.parse(localStorage.getItem('admin_user') || '{}');

  // Handle Window Resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false); // Force close on resize to mobile
      } else {
        setSidebarOpen(true); // Force open on desktop
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_user');
    navigate('/xcbdsll/admn/lgn');
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: <LayoutDashboard size={20} />, label: 'Dashboard' },
    { path: '/admin/media-library', icon: <FolderOpen size={20} />, label: 'Media Library' },
    { path: '/admin/gallery', icon: <Image size={20} />, label: 'Galeri' },
    { path: '/admin/team', icon: <Users size={20} />, label: 'Tim & Pengurus' },
    { path: '/admin/articles', icon: <FileText size={20} />, label: 'Artikel' },
    { path: '/admin/static-content', icon: <Type size={20} />, label: 'Statistik' },
    { path: '/admin/loker', icon: <Briefcase size={20} />, label: 'Loker' },
    { path: '/admin/applicants', icon: <UserPlus size={20} />, label: 'Pelamar' }, // New Menu
    { path: '/admin/settings', icon: <Settings size={20} />, label: 'Pengaturan' },
  ];

  const styles = {
    layout: {
      display: 'flex',
      height: '100vh',
      backgroundColor: '#f9fafb',
      overflow: 'hidden',
      position: 'relative'
    },
    sidebar: {
      width: sidebarOpen ? '260px' : '0',
      backgroundColor: '#ffffff', // Base white color
      // Subtle diagonal line pattern for texture
      backgroundImage: `linear-gradient(45deg, rgba(240, 240, 240, 0.5) 25%, transparent 25%, transparent 75%, rgba(240, 240, 240, 0.5) 75%, rgba(240, 240, 240, 0.5)),
                        linear-gradient(-45deg, rgba(240, 240, 240, 0.5) 25%, transparent 25%, transparent 75%, rgba(240, 240, 240, 0.5) 75%, rgba(240, 240, 240, 0.5))`,
      backgroundSize: '20px 20px', // Size of each pattern square
      backgroundPosition: '0 0, 10px 10px', // Offset for second pattern to create cross-hatch
      borderRight: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.3s ease',
      position: isMobile ? 'absolute' : 'relative', // Overlay on mobile
      height: '100%',
      zIndex: 50,
      overflowX: 'hidden', // Prevent content spill
      whiteSpace: 'nowrap', // Prevent text wrapping
      boxShadow: isMobile && sidebarOpen ? '4px 0 15px rgba(0,0,0,0.1)' : 'none'
    },
    logoArea: {
      padding: '1.5rem',
      // borderBottom: '1px solid #e5e7eb', // Removed border for cleaner look
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      minWidth: '260px', // Ensure content doesn't squeeze
      background: 'rgba(255,255,255,0.8)', // Slight transparency
      backdropFilter: 'blur(4px)'
    },
    logoIconContainer: {
      position: 'relative',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '12px'
    },
    logoBgShape: {
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: '#4f46e5', // indigo-600
      borderRadius: '8px',
      transform: 'rotate(45deg)'
    },
    logoIconWrapper: {
      position: 'relative',
      zIndex: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    logoMainText: {
      fontSize: '1.25rem', // text-xl
      fontWeight: '700',   // font-bold
      background: 'linear-gradient(to right, #4f46e5, #9333ea, #4338ca)', 
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      lineHeight: 1,
      fontFamily: 'sans-serif'
    },
    logoSubText: {
      fontSize: '0.75rem', // text-xs
      color: '#374151',    // text-gray-700
      letterSpacing: '0.1em', // tracking-widest
      fontWeight: '500',   // font-medium
      marginTop: '4px'
    },
    nav: {
      flex: 1,
      padding: '1.5rem 1rem',
      overflowY: 'auto',
      minWidth: '260px' // Ensure content doesn't squeeze
    },
    navItem: (isActive) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem 1rem',
      borderRadius: '0.5rem',
      marginBottom: '0.25rem',
      color: isActive ? '#2563eb' : '#4b5563',
      backgroundColor: isActive ? '#eff6ff' : 'transparent',
      textDecoration: 'none',
      fontSize: '0.95rem',
      fontWeight: isActive ? '600' : '500',
      transition: 'all 0.2s'
    }),
    userArea: {
      padding: '1rem',
      borderTop: '1px solid #e5e7eb',
      minWidth: '260px'
    },
    logoutBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      width: '100%',
      padding: '0.75rem 1rem',
      border: 'none',
      background: 'none',
      color: '#ef4444',
      cursor: 'pointer',
      fontSize: '0.95rem',
      fontWeight: '500'
    },
    main: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative'
    },
    header: {
      height: '64px',
      backgroundColor: '#fff',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      padding: '0 2rem',
      justifyContent: 'space-between'
    },
    toggleBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#4b5563',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: isMobile ? '1rem' : '2rem'
    },
    userName: {
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#111'
    },
    // Mobile Overlay Backdrop
    backdrop: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.3)',
      zIndex: 40,
      display: isMobile && sidebarOpen ? 'block' : 'none'
    }
  };

  return (
    <div style={styles.layout}>
      {/* Mobile Backdrop */}
      <div style={styles.backdrop} onClick={() => setSidebarOpen(false)}></div>

      {/* Sidebar */}
      <aside style={styles.sidebar}>
        <div style={styles.logoArea}>
          <div style={{display: 'flex', alignItems: 'center'}}>
            <div style={styles.logoIconContainer}>
              <div style={styles.logoBgShape} />
              <div style={styles.logoIconWrapper}>
                <Database size={20} color="#ffffff" />
              </div>
            </div>
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <span style={styles.logoMainText}>IMOONUSEL</span>
              <span style={styles.logoSubText}>ADMIN PANEL</span>
            </div>
          </div>

          {/* Close button inside sidebar for mobile */}
          {isMobile && (
            <button onClick={() => setSidebarOpen(false)} style={styles.toggleBtn}>
              <X size={20} />
            </button>
          )}
        </div>
        
        <nav style={styles.nav}>
          {menuItems.map((item) => (
            <Link 
              key={item.path} 
              to={item.path} 
              onClick={() => isMobile && setSidebarOpen(false)} // Close on click (mobile)
              style={styles.navItem(location.pathname === item.path)}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div style={styles.userArea}>
          <button onClick={handleLogout} style={styles.logoutBtn}>
            <LogOut size={20} />
            <span>Keluar</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div style={styles.main}>
        <header style={styles.header}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={styles.toggleBtn}>
            <AlignLeft size={24} />
          </button>
          <div style={styles.userName}>
            Halo, {user.name || 'Admin'}
          </div>
        </header>
        
        <main style={styles.content}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;